import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import api from '../utils/api';
import { Users, Plus, X, Crown, Settings, ArrowRight, Trash2, Search, MapPin, Send, LogOut, Mail, Check } from 'lucide-react';
import { LocationPicker } from '../components/LocationPicker';

export default function ClubsPage({ onSelectClub }) {
  const { user, logout } = useAuth();
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [discoverClubs, setDiscoverClubs] = useState([]);
  const [showDiscover, setShowDiscover] = useState(false);
  const [requesting, setRequesting] = useState(null);
  const [createLoc, setCreateLoc] = useState({ country: '', state: '', city: '' });
  const [discoverFilter, setDiscoverFilter] = useState({ country: '', state: '', city: '' });
  const [pendingInvites, setPendingInvites] = useState([]);
  const [inviteBusy, setInviteBusy] = useState(null);

  const fetchClubs = async () => {
    try {
      const res = await api.get('/clubs');
      setClubs(res.data);
    } catch (err) {
      toast.error('Failed to load clubs');
    } finally {
      setLoading(false);
    }
  };

  const fetchInvites = async () => {
    try {
      const res = await api.get('/clubs/my-invites');
      const incoming = (res.data?.incoming || []).filter(i => i.status === 'pending');
      setPendingInvites(incoming);
    } catch {
      setPendingInvites([]);
    }
  };

  useEffect(() => {
    fetchClubs();
    fetchInvites();
    // Also fetch discoverable clubs
    api.get('/clubs/discover').then(r => setDiscoverClubs(r.data || [])).catch(() => {});
  }, []);

  const acceptInvite = async (inv) => {
    setInviteBusy(inv.id);
    try {
      const res = await api.post(`/clubs/invites/${inv.id}/accept`);
      toast.success(res.data?.message || `Joined ${inv.club_name}!`);
      await Promise.all([fetchClubs(), fetchInvites()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept invite');
    } finally {
      setInviteBusy(null);
    }
  };

  const rejectInvite = async (inv) => {
    setInviteBusy(inv.id);
    try {
      await api.post(`/clubs/invites/${inv.id}/reject`);
      toast.success('Invite declined');
      await fetchInvites();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to decline invite');
    } finally {
      setInviteBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="clubs-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              My Clubs
            </h1>
            <p className="text-sm text-slate-500 mt-1">Select a club to manage or create a new one</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all"
              data-testid="create-club-btn"
            >
              <Plus size={18} />
              Create Club
            </button>
            <button
              onClick={() => { logout(); window.location.href = '/login'; }}
              className="flex items-center gap-2 px-3 py-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium transition-all"
              data-testid="clubs-page-logout-btn"
              title="Logout"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>

        {/* Pending Club Invites */}
        {pendingInvites.length > 0 && (
          <div className="mb-6" data-testid="pending-invites-section">
            <div className="flex items-center gap-2 mb-2">
              <Mail size={16} className="text-amber-600" />
              <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                {pendingInvites.length === 1 ? 'You have a club invite' : `You have ${pendingInvites.length} club invites`}
              </h2>
            </div>
            <div className="space-y-2">
              {pendingInvites.map(inv => (
                <div
                  key={inv.id}
                  className="bg-white rounded-xl border border-amber-200 p-4 flex items-center gap-4"
                  data-testid={`pending-invite-${inv.id}`}
                >
                  <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
                    <Mail size={18} className="text-amber-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">{inv.club_name}</p>
                    <p className="text-xs text-slate-500 truncate">
                      {inv.message ? inv.message : 'You\u2019ve been invited to join this club'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => acceptInvite(inv)}
                      disabled={inviteBusy === inv.id}
                      className="h-8 px-3 bg-green-600 hover:bg-green-700 disabled:bg-slate-300 text-white text-xs font-semibold rounded-lg flex items-center gap-1"
                      data-testid={`accept-invite-${inv.id}`}
                    >
                      <Check size={12} /> {inviteBusy === inv.id ? '...' : 'Join'}
                    </button>
                    <button
                      onClick={() => rejectInvite(inv)}
                      disabled={inviteBusy === inv.id}
                      className="h-8 w-8 bg-white border border-slate-200 hover:bg-slate-50 text-slate-500 rounded-lg flex items-center justify-center"
                      title="Decline"
                      data-testid={`reject-invite-${inv.id}`}
                    >
                      <X size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Clubs List */}
        {clubs.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
            <Users size={48} className="mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No clubs yet</h3>
            <p className="text-sm text-slate-500 mb-6">
              {pendingInvites.length > 0
                ? 'Accept an invite above to join an existing club, or create your own.'
                : 'Create a club to start managing tournaments'}
            </p>
            <button
              onClick={() => setShowCreate(true)}
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-2.5 rounded-lg text-sm font-medium transition-all"
            >
              Create Your First Club
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {clubs.map(g => (
              <div
                key={g.id}
                onClick={() => onSelectClub(g)}
                className="bg-white rounded-xl border border-slate-200 p-4 hover:border-green-300 hover:shadow-md cursor-pointer transition-all group"
                data-testid={`club-card-${g.id}`}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                    <Users size={22} className="text-green-700" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-slate-900">{g.name}</h3>
                      {g.my_role === 'admin' && (
                        <span className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                          <Crown size={10} /> Admin
                        </span>
                      )}
                      {g.my_role === 'contributor' && (
                        <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                          Contributor
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-500">
                      {g.member_count} member{g.member_count !== 1 ? 's' : ''} 
                      {g.description && ` · ${g.description}`}
                    </p>
                  </div>
                  <ArrowRight size={20} className="text-slate-300 group-hover:text-green-600 transition-colors" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Discover Clubs */}
        <div className="mt-8">
          <button onClick={() => setShowDiscover(!showDiscover)}
            className="flex items-center gap-2 text-sm font-medium text-green-600 hover:text-green-700 transition-all mb-3"
            data-testid="toggle-discover-clubs">
            <Search size={14} />
            {showDiscover ? 'Hide' : 'Discover'} Other Clubs
          </button>
          {showDiscover && (
            <div className="space-y-3">
              <div className="bg-white rounded-xl border border-slate-200 p-3">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Filter by Location</p>
                <LocationPicker compact
                  country={discoverFilter.country} state={discoverFilter.state} city={discoverFilter.city}
                  onChange={f => {
                    setDiscoverFilter(f);
                    const params = new URLSearchParams();
                    if (f.country) params.set('country', f.country);
                    if (f.state) params.set('state', f.state);
                    if (f.city) params.set('city', f.city);
                    api.get(`/clubs/discover?${params}`).then(r => setDiscoverClubs(r.data || [])).catch(() => {});
                  }}
                />
              </div>
              {discoverClubs.filter(c => !c.is_member).length === 0 ? (
                <p className="text-sm text-slate-400 py-4 text-center">No other clubs to discover</p>
              ) : (
                discoverClubs.filter(c => !c.is_member).map(c => (
                  <div key={c.id} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4"
                    data-testid={`discover-club-${c.id}`}>
                    <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                      <Users size={18} className="text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900">{c.name}</p>
                      <div className="flex items-center gap-3 mt-0.5 text-xs text-slate-500">
                        <span>{c.member_count} members</span>
                        <span>Rating: {c.rating_range.min}–{c.rating_range.max}</span>
                        {(c.city || c.state || c.country) && <span className="flex items-center gap-0.5"><MapPin size={10} />{[c.city, c.state, c.country].filter(Boolean).join(', ')}</span>}
                      </div>
                    </div>
                    <button
                      onClick={async () => {
                        setRequesting(c.id);
                        try {
                          await api.post(`/clubs/${c.id}/request-join`, { message: '' });
                          toast.success(`Join request sent to ${c.name}`);
                          setDiscoverClubs(dc => dc.map(d => d.id === c.id ? {...d, requested: true} : d));
                        } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                        finally { setRequesting(null); }
                      }}
                      disabled={requesting === c.id || c.requested}
                      className="h-8 px-3 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white text-xs font-semibold rounded-lg transition-all flex items-center gap-1 flex-shrink-0"
                      data-testid={`request-join-${c.id}`}>
                      <Send size={11} /> {c.requested ? 'Requested' : 'Request to Join'}
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Create Club Modal */}
      {showCreate && (
        <CreateClubModal 
          onClose={() => setShowCreate(false)} 
          onCreated={() => {
            setShowCreate(false);
            fetchClubs();
          }} 
        />
      )}
    </div>
  );
}

function CreateClubModal({ onClose, onCreated }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [loc, setLoc] = useState({ country: '', state: '', city: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Club name is required');
      return;
    }
    setLoading(true);
    try {
      await api.post('/clubs', { name: name.trim(), description, ...loc });
      toast.success(`Club "${name}" created!`);
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create club');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Create Club
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="create-club-form">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Club Name</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Badminton Club"
              className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              data-testid="club-name-input"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Description (optional)</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="What's this club for?"
              rows={2}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
              data-testid="club-description-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Club Location</label>
            <LocationPicker country={loc.country} state={loc.state} city={loc.city} onChange={setLoc} />
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 h-10 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium rounded-lg transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 h-10 bg-green-600 hover:bg-green-700 disabled:bg-slate-300 text-white text-sm font-semibold rounded-lg transition-all"
              data-testid="submit-create-club-btn"
            >
              {loading ? 'Creating...' : 'Create Club'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
